/**
 * services/scrap.service.ts - Služby pro analýzu zmetkovitosti (scrap)
 * 
 * FUNKCE:
 * - Souhrnná statistika zmetků (počet, míra, změna oproti předchozímu období)
 * - Denní trendy zmetkovitosti (celkem i po různých dimenzích)
 * - Top důvody zmetků
 * - Heatmapy zmetkovitosti podle dnů v týdnu a hodin
 * 
 * ZDROJE DAT:
 * - Shifts (okCount, nokCount) - základní data o produkci
 * - ScrapRecords - detailní záznamy o zmetcích s důvody
 * 
 * KLÍČOVÉ METRIKY:
 * - Scrap Rate (míra zmetků) = NOK / (OK + NOK)
 * - Scrap Count (počet zmetků) = NOK
 * - Scrap Costs (náklady na zmetky) = suma(scrapCount * scrapCostsPerUnit)
 * 
 * VÝROBNÍ DNY:
 * Všechny denní agregace používají productionDayKey(), takže respektují výrobní den
 * definovaný v TenantModel.dayStart.
 */

import moment from 'moment';
import { ShiftsRepository } from '@/libs/tpm-analytics/data/shifts.repository';
import { ScrapRecordsRepository } from '@/libs/tpm-analytics/data/scrap-records.repository';
import { createGrid, topNIndexes } from '@/libs/tpm-analytics/core/aggregation.utils';
import { productionDayKey } from '@/libs/tpm-analytics/core/production-day.utils';
import { calcScrapCostKc } from '@/libs/tpm-analytics/core/financial.calculator';
import { stationsStore } from '@/libs/stations/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { StationModel, ProductModel } from '@/libs/Api';
import { productsStore } from '@/libs/products/+state/store';
import { buildBomTree, flattenBomTree, getUniqueProductIds, getMaxDepth } from '@/libs/tpm-analytics/core/bom-tree.utils';
import {
    ScrapSummary,
    ScrapTrendPoint,
    ScrapTrendByProjectSeries,
    ScrapTrendByWorkplaceSeries,
    ScrapTrendByShiftTeamSeries,
    ScrapTrendByShiftTypeSeries,
    TopScrapReasonItem,
    ScrapHeatmapResult,
    ScrapAggregationDimension,
    AggregatedScrapItem,
    ProductionProcessInternal,
    ProcessStepInternal,
} from '@/libs/tpm-analytics/core/types';

/**
 * INTERNÍ: Vypočítá kumulovanou zmetkovitost procesů z dat o produktech
 * 
 * VÝPOČET:
 * 1. Pro každý stupeň procesu vypočítat good rate = OK / (OK + NOK)
 * 2. Kumulovaná good rate = násobek všech good rates v procesu
 * 3. Vážený průměr good rates ze všech procesů (váha = množství vstupního produktu)
 * 4. Finální scrap rate = 1 - vážený průměr good rate
 * 
 * @param productStats Map produktId -> { okCount, nokCount }
 * @param allProducts Všechny produkty včetně kusovníků
 * @returns Celková zmetkovitost vypočítaná z good rates
 */
function calculateProcessBasedScrapRate(
    productStats: Map<string, { ok: number; nok: number }>,
    allProducts: ProductModel[]
): { scrapRate: number | null; totalWeight: number; processCount: number } {
    console.log('[Scrap] Calculating process-based scrap rate...');
    
    // 1. Najít všechny produkty s kusovníkem (= výstupy procesů)
    const outputProducts = allProducts.filter(p => 
        p.billOfMaterialItems && p.billOfMaterialItems.length > 0
    );
    
    console.log(`[Scrap] Found ${outputProducts.length} products with BOM`);
    
    if (outputProducts.length === 0) {
        // Fallback na jednoduchý výpočet (žádné procesy)
        console.log('[Scrap] No processes found, using simple calculation');
        return calculateSimpleScrapRate(productStats);
    }
    
    // 2. Pro každý proces spočítat kumulovanou zmetkovitost
    const processes: ProductionProcessInternal[] = [];
    
    for (const outProduct of outputProducts) {
        const tree = buildBomTree(outProduct.id!, allProducts);
        if (!tree) {
            console.warn(`[Scrap] Failed to build BOM tree for ${outProduct.name}`);
            continue;
        }
        
        const flatNodes = flattenBomTree(tree);
        const maxDepth = getMaxDepth(tree);
        
        // Spočítat kumulovanou GOOD RATE (násobek procent dobrých kusů na všech stupních)
        let cumulativeGoodRate: number | null = 1.0;
        let hasAllData = true;
        
        const steps: ProcessStepInternal[] = [];
        
        for (const node of flatNodes) {
            const stats = productStats.get(node.productId);
            
            if (!stats) {
                hasAllData = false;
                continue;
            }
            
            const total = stats.ok + stats.nok;
            const goodRate = total > 0 ? stats.ok / total : null; // ZMĚNA: OK / (OK + NOK)
            const scrapRate = total > 0 ? stats.nok / total : null;
            
            if (goodRate === null) {
                hasAllData = false;
            } else if (cumulativeGoodRate !== null) {
                cumulativeGoodRate *= goodRate; // ZMĚNA: násobíme good rate, ne scrap rate
            }
            
            steps.push({
                productId: node.productId,
                depth: node.depth,
                scrapRate,
                okCount: stats.ok,
                nokCount: stats.nok,
                totalCount: stats.ok + stats.nok
            });
        }
        
        if (!hasAllData) {
            console.log(`[Scrap] Incomplete data for process ${outProduct.name}, skipping`);
            cumulativeGoodRate = null;
        }
        
        // Najít vstupní produkt (ten s max depth) pro váhu
        const inputStep = steps.find(s => s.depth === maxDepth);
        const inputQuantity = inputStep?.totalCount ?? 0;
        
        // ZMĚNA: Ukládáme cumulativeGoodRate (ne scrapRate)
        if (cumulativeGoodRate !== null && inputQuantity > 0) {
            processes.push({
                outputProductId: outProduct.id!,
                steps,
                cumulativeScrapRate: cumulativeGoodRate, // dočasně v tomto poli, později přepočítáme
                inputProductQuantity: inputQuantity
            });
            
            console.log(`[Scrap] Process ${outProduct.name}: goodRate=${(cumulativeGoodRate * 100).toFixed(4)}%, weight=${inputQuantity}`);
        }
    }
    
    // 3. Spočítat vážený průměr GOOD RATE
    let weightedSum = 0;
    let totalWeight = 0;
    
    for (const proc of processes) {
        // cumulativeScrapRate obsahuje ve skutečnosti cumulativeGoodRate (viz výše)
        if (proc.cumulativeScrapRate !== null && proc.inputProductQuantity > 0) {
            weightedSum += proc.cumulativeScrapRate * proc.inputProductQuantity; // good rate × váha
            totalWeight += proc.inputProductQuantity;
        }
    }
    
    const weightedAvgGoodRate = totalWeight > 0 ? weightedSum / totalWeight : null;
    
    // 4. Vypočítat celkovou SCRAP RATE jako 1 - vážený průměr good rate
    const scrapRate = weightedAvgGoodRate !== null ? 1 - weightedAvgGoodRate : null;
    
    console.log(`[Scrap] Weighted average good rate: ${weightedAvgGoodRate !== null ? (weightedAvgGoodRate * 100).toFixed(4) : 'null'}%`);
    console.log(`[Scrap] Final scrap rate: ${scrapRate !== null ? (scrapRate * 100).toFixed(4) : 'null'}%, total weight: ${totalWeight}`);
    
    return {
        scrapRate,
        totalWeight,
        processCount: processes.length
    };
}

/**
 * INTERNÍ: Jednoduchý výpočet zmetkovitosti (fallback když nejsou procesy)
 * 
 * Používá se když v databázi nejsou žádné produkty s kusovníkem.
 * Prostě sečte všechny OK a NOK a vydělí.
 */
function calculateSimpleScrapRate(
    productStats: Map<string, { ok: number; nok: number }>
): { scrapRate: number | null; totalWeight: number; processCount: number } {
    let totalOk = 0;
    let totalNok = 0;
    
    for (const stats of productStats.values()) {
        totalOk += stats.ok;
        totalNok += stats.nok;
    }
    
    const total = totalOk + totalNok;
    const scrapRate = total > 0 ? totalNok / total : null;
    
    return {
        scrapRate,
        totalWeight: total,
        processCount: 0
    };
}

/**
 * Shrnutí zmetkovitosti (počet NOK, míra, meziobdobní změna, náklady).
 * 
 * ZMĚNA: Zmetkovitost nyní zohledňuje procesy definované kusovníkem.
 * Výpočet: násobí se good rates (OK/(OK+NOK)) na všech stupních procesu,
 * výsledkem je vážený průměr, a finální scrap rate = 1 - avg good rate.
 * 
 * Porovnává aktuální období s předchozím obdobím stejné délky.
 * Náklady na zmetky se počítají ze ScrapRecords (scrapCount * scrapCostsPerUnit).
 * 
 * @param fromIso Začátek období
 * @param toIso Konec období
 * @param filters Filtry (locationIds, workplaceIds, projectIds, shiftTypeIds, shiftTeamTypeIds)
 * @returns {totalScraps, scrapRate, scrapRateDeltaPct, scrapCosts, avgCostPerPiece}
 */
export async function getScrapSummary(fromIso: string, toIso: string, filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }): Promise<ScrapSummary> {
    // Načteme shifts pro počty OK/NOK, ScrapRecords pro náklady a produkty pro kusovníky
    const [shiftsNow, shiftsPrev, scrapRecsNow, allProducts] = await (async () => {
        const from = moment(fromIso);
        const to = moment(toIso);
        const span = Math.max(1, to.diff(from, 'minutes'));
        const prevTo = from.clone();
        const prevFrom = from.clone().add(-span, 'minutes');
        const [now, prev, scraps, products] = await Promise.all([
            ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters),
            ShiftsRepository.fetchShiftsForRange([], prevFrom.toISOString(), prevTo.toISOString(), filters),
            ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, filters),
            Promise.resolve((productsStore.useGetter(CrudGetter.Data) as ProductModel[]) ?? [])
        ]);
        return [now, prev, scraps, products];
    })();

    // NOVÁ AGREGACE: Podle PRODUKTŮ místo podle shifts
    const aggregateByProduct = (shifts: any[]) => {
        const productMap = new Map<string, { ok: number; nok: number }>();
        
        for (const shift of shifts) {
            const productions = (shift as any).shiftProductions || [];
            
            for (const prod of productions) {
                const pid = prod?.productId;
                if (!pid) continue;
                
                if (!productMap.has(pid)) {
                    productMap.set(pid, { ok: 0, nok: 0 });
                }
                
                const stats = productMap.get(pid)!;
                stats.ok += Number(prod?.okCount ?? 0) || 0;
                stats.nok += Number(prod?.nokCount ?? 0) || 0;
            }
        }
        
        return productMap;
    };

    const curProductStats = aggregateByProduct(shiftsNow);
    const prevProductStats = aggregateByProduct(shiftsPrev);
    
    // NOVÝ VÝPOČET: Procesní zmetkovitost s ohledem na kusovníky
    const curResult = calculateProcessBasedScrapRate(curProductStats, allProducts);
    const prevResult = calculateProcessBasedScrapRate(prevProductStats, allProducts);
    
    const scrapRate = curResult.scrapRate;
    const prevRate = prevResult.scrapRate;
    const scrapRateDeltaPct = (scrapRate != null && prevRate != null) ? +(((scrapRate - prevRate) * 100)).toFixed(1) : null;
    
    // Celkový počet NOK (sečteme ze všech produktů - zůstává stejný)
    const totalNok = Array.from(curProductStats.values()).reduce((sum, s) => sum + s.nok, 0);
    
    // Výpočet nákladů na zmetky ze ScrapRecords (beze změny)
    const scrapCosts = scrapRecsNow.reduce((sum, r: any) => sum + calcScrapCostKc(r), 0);
    const avgCostPerPiece = totalNok > 0 ? scrapCosts / totalNok : 0;

    // Kompletnost dat (beze změny)
    const totalScrapRecords = scrapRecsNow.length;
    const scrapRecordsWithCost = scrapRecsNow.filter((r: any) => {
        const unitCost = Number(r?.scrapCostsPerUnit ?? r?.product?.scrapCosts ?? 0) || 0;
        return unitCost > 0;
    }).length;
    const scrapCompletenessPercent = totalScrapRecords > 0 
        ? Math.round((scrapRecordsWithCost / totalScrapRecords) * 100) 
        : 100;
    
    console.log(`[Scrap] Summary: rate=${scrapRate !== null ? (scrapRate * 100).toFixed(4) : 'null'}%, total NOK=${totalNok}, processes=${curResult.processCount}`);
    
    return { 
        totalScraps: totalNok, 
        scrapRate, 
        scrapRateDeltaPct, 
        scrapCosts, 
        avgCostPerPiece,
        scrapDataCompleteness: {
            totalRecords: totalScrapRecords,
            recordsWithCost: scrapRecordsWithCost,
            completenessPercent: scrapCompletenessPercent,
        },
    };
}

/**
 * Denní trend míry zmetků s procesním výpočtem.
 * 
 * ZMĚNA: Nyní zohledňuje procesy definované kusovníkem.
 * Pro každý den se násobí good rates, váží dle vstupního množství,
 * a finální scrap rate = 1 - vážený průměr good rate.
 * 
 * Používá výrobní den definovaný v TenantModel.dayStart.
 */
export async function getScrapTrendDaily(fromIso: string, toIso: string, filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }): Promise<ScrapTrendPoint[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    const allProducts = (productsStore.useGetter(CrudGetter.Data) as ProductModel[]) ?? [];
    
    // Agregace podle dnů a produktů
    type DayProductKey = string; // "day|productId"
    const dailyProductStats = new Map<DayProductKey, { ok: number; nok: number }>();
    const days = new Set<string>();
    
    for (const s of shifts) {
        if (!s?.fromDate) continue;
        const day = productionDayKey(Date.parse(s.fromDate as string));
        days.add(day);
        
        const productions = (s as any).shiftProductions || [];
        for (const prod of productions) {
            const pid = prod?.productId;
            if (!pid) continue;
            
            const key = `${day}|${pid}`;
            if (!dailyProductStats.has(key)) {
                dailyProductStats.set(key, { ok: 0, nok: 0 });
            }
            
            const stats = dailyProductStats.get(key)!;
            stats.ok += Number(prod?.okCount ?? 0) || 0;
            stats.nok += Number(prod?.nokCount ?? 0) || 0;
        }
    }
    
    // Pro každý den spočítat procesní zmetkovitost
    const sortedDays = Array.from(days).sort();
    const result: ScrapTrendPoint[] = [];
    
    for (const day of sortedDays) {
        // Sestavit productStats pro tento den
        const dayProductStats = new Map<string, { ok: number; nok: number }>();
        
        for (const [key, stats] of dailyProductStats.entries()) {
            const [d, pid] = key.split('|');
            if (d === day) {
                dayProductStats.set(pid, stats);
            }
        }
        
        const dayResult = calculateProcessBasedScrapRate(dayProductStats, allProducts);
        
        result.push({
            day,
            rate: dayResult.scrapRate
        });
    }
    
    return result;
}

/**
 * Denní trend míry zmetků podle projektu – data bereme z `shiftProductions`.
 * Používá výrobní den definovaný v TenantModel.dayStart.
 */
export async function getScrapTrendDailyByProject(fromIso: string, toIso: string, filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }): Promise<ScrapTrendByProjectSeries[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    type Acc = { ok: number; nok: number };
    const map = new Map<string, Map<string | null, Acc>>();
    const nameMap = new Map<string | null, string>();
    for (const s of shifts) {
        const day = s?.fromDate ? productionDayKey(Date.parse(s.fromDate as string)) : null; // výrobní den dle směny
        if (!day) continue;
        const prods: any[] = ((s as any).shiftProductions as any[]) || [];
        if (prods.length === 0) continue;
        if (!map.has(day)) map.set(day, new Map());
        const byProj = map.get(day)!;
        for (const p of prods) {
            const pid: string | null = p?.projectId ?? null;
            const pname: string = p?.project?.name ?? '—';
            nameMap.set(pid, pname);
            if (!byProj.has(pid)) byProj.set(pid, { ok: 0, nok: 0 });
            const acc = byProj.get(pid)!;
            acc.ok += Number(p?.okCount ?? 0) || 0;
            acc.nok += Number(p?.nokCount ?? 0) || 0;
        }
    }
    const days = Array.from(map.keys()).sort();
    const projectIds = new Set<string | null>();
    for (const d of days) for (const pid of map.get(d)!.keys()) projectIds.add(pid);
    const series: ScrapTrendByProjectSeries[] = [];
    for (const pid of projectIds) {
        const points: ScrapTrendPoint[] = days.map((d) => {
            const acc = map.get(d)!.get(pid) || { ok: 0, nok: 0 };
            const total = acc.ok + acc.nok;
            const rate = total > 0 ? acc.nok / total : null;
            return { day: d, rate } as ScrapTrendPoint;
        });
        series.push({ projectId: pid, projectName: nameMap.get(pid) || '—', points });
    }
    return series.sort((a, b) => a.projectName.localeCompare(b.projectName));
}

/**
 * Denní trend míry zmetků podle pracoviště.
 * Používá výrobní den definovaný v TenantModel.dayStart.
 */
export async function getScrapTrendDailyByWorkplace(fromIso: string, toIso: string, filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }): Promise<ScrapTrendByWorkplaceSeries[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    type Acc = { ok: number; nok: number };
    const map = new Map<string, Map<string | null, Acc>>();
    const nameMap = new Map<string | null, string>();
    for (const s of shifts) {
        const day = s?.fromDate ? productionDayKey(Date.parse(s.fromDate as string)) : null;
        if (!day) continue;
        const wid: string | null = (s as any)?.workplaceId ?? (s as any)?.workplace?.id ?? null;
        const wname: string = ((s as any)?.workplace?.name as any) || '—';
        if (!map.has(day)) map.set(day, new Map());
        const byWp = map.get(day)!;
        nameMap.set(wid, wname);
        if (!byWp.has(wid)) byWp.set(wid, { ok: 0, nok: 0 });
        const acc = byWp.get(wid)!;
        acc.ok += Number((s as any)?.okCount ?? 0) || 0;
        acc.nok += Number((s as any)?.nokCount ?? 0) || 0;
    }
    const days = Array.from(map.keys()).sort();
    const workplaceIds = new Set<string | null>();
    for (const d of days) for (const wid of map.get(d)!.keys()) workplaceIds.add(wid);
    const series: ScrapTrendByWorkplaceSeries[] = [];
    for (const wid of workplaceIds) {
        const points: ScrapTrendPoint[] = days.map((d) => {
            const acc = map.get(d)!.get(wid) || { ok: 0, nok: 0 };
            const total = acc.ok + acc.nok;
            const rate = total > 0 ? acc.nok / total : null;
            return { day: d, rate } as ScrapTrendPoint;
        });
        series.push({ workplaceId: wid, workplaceName: nameMap.get(wid) || '—', points });
    }
    return series.sort((a, b) => a.workplaceName.localeCompare(b.workplaceName));
}

/**
 * Denní trend míry zmetků podle směnového týmu.
 * Používá výrobní den definovaný v TenantModel.dayStart.
 */
export async function getScrapTrendDailyByShiftTeamType(fromIso: string, toIso: string, filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }): Promise<ScrapTrendByShiftTeamSeries[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    type Acc = { ok: number; nok: number };
    const map = new Map<string, Map<string | null, Acc>>();
    const nameMap = new Map<string | null, string>();
    for (const s of shifts) {
        const day = s?.fromDate ? productionDayKey(Date.parse(s.fromDate as string)) : null;
        if (!day) continue;
        const teamId: string | null = (s as any).shiftTeamTypeId ?? null;
        const teamName: string = ((s as any).shiftTeamType?.name as any) || '—';
        if (!map.has(day)) map.set(day, new Map());
        const byTeam = map.get(day)!;
        nameMap.set(teamId, teamName);
        if (!byTeam.has(teamId)) byTeam.set(teamId, { ok: 0, nok: 0 });
        const acc = byTeam.get(teamId)!;
        acc.ok += Number((s as any)?.okCount ?? 0) || 0;
        acc.nok += Number((s as any)?.nokCount ?? 0) || 0;
    }
    const days = Array.from(map.keys()).sort();
    const teamIds = new Set<string | null>();
    for (const d of days) for (const id of map.get(d)!.keys()) teamIds.add(id);
    const series: ScrapTrendByShiftTeamSeries[] = [];
    for (const id of teamIds) {
        const points: ScrapTrendPoint[] = days.map((d) => {
            const acc = map.get(d)!.get(id) || { ok: 0, nok: 0 };
            const total = acc.ok + acc.nok;
            const rate = total > 0 ? acc.nok / total : null;
            return { day: d, rate } as ScrapTrendPoint;
        });
        series.push({ shiftTeamTypeId: id, shiftTeamTypeName: nameMap.get(id) || '—', points });
    }
    return series.sort((a, b) => a.shiftTeamTypeName.localeCompare(b.shiftTeamTypeName));
}

/**
 * Denní trend míry zmetků podle typu směny.
 * Používá výrobní den definovaný v TenantModel.dayStart.
 */
export async function getScrapTrendDailyByShiftType(fromIso: string, toIso: string, filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }): Promise<ScrapTrendByShiftTypeSeries[]> {
    const shifts = await ShiftsRepository.fetchShiftsForRange([], fromIso, toIso, filters);
    type Acc = { ok: number; nok: number };
    const map = new Map<string, Map<string | null, Acc>>();
    const nameMap = new Map<string | null, string>();
    for (const s of shifts) {
        const day = s?.fromDate ? productionDayKey(Date.parse(s.fromDate as string)) : null;
        if (!day) continue;
        const id: string | null = (s as any).shiftTypeId ?? null;
        const name: string = ((s as any).shiftType?.name as any) || '—';
        if (!map.has(day)) map.set(day, new Map());
        const byId = map.get(day)!;
        nameMap.set(id, name);
        if (!byId.has(id)) byId.set(id, { ok: 0, nok: 0 });
        const acc = byId.get(id)!;
        acc.ok += Number((s as any)?.okCount ?? 0) || 0;
        acc.nok += Number((s as any)?.nokCount ?? 0) || 0;
    }
    const days = Array.from(map.keys()).sort();
    const ids = new Set<string | null>();
    for (const d of days) for (const id of map.get(d)!.keys()) ids.add(id);
    const series: ScrapTrendByShiftTypeSeries[] = [];
    for (const id of ids) {
        const points: ScrapTrendPoint[] = days.map((d) => {
            const acc = map.get(d)!.get(id) || { ok: 0, nok: 0 };
            const total = acc.ok + acc.nok;
            const rate = total > 0 ? acc.nok / total : null;
            return { day: d, rate } as ScrapTrendPoint;
        });
        series.push({ shiftTypeId: id, shiftTypeName: nameMap.get(id) || '—', points });
    }
    return series.sort((a, b) => a.shiftTypeName.localeCompare(b.shiftTypeName));
}

/**
 * Heatmapa zmetků (součet NOK) po dnech v týdnu × hodinách.
 * Pozn.: používáme jednotlivé záznamy zmetků (preciznější než agregace ze směn).
 */
export async function getScrapHeatmap(fromIso: string, toIso: string, filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }): Promise<ScrapHeatmapResult> {
    const records: any[] = await ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, filters);
    const grid: number[][] = createGrid(7, 24, 0);
    let totalScraps = 0;
    for (const r of records) {
        if (!r?.recordTime) continue;
        const m = moment(r.recordTime).local();
        const dIdx = m.isoWeekday() - 1;
        const h = m.hour();
        const sc = Number(r?.scrapCount ?? 0) || 0;
        if (dIdx >= 0 && dIdx < 7 && h >= 0 && h < 24) grid[dIdx][h] += sc;
        totalScraps += sc;
    }
    const from = moment(fromIso);
    const to = moment(toIso);
    const daysInSpan = Math.max(1, to.diff(from, 'days') + 1);
    const avgPerDay = Math.round(totalScraps / daysInSpan);
    const dayTotals = grid.map((r) => r.reduce((a, b) => a + b, 0));
    const criticalDayIndexes = dayTotals.map((v, i) => ({ v, i })).filter((x) => x.v > 0).sort((a, b) => b.v - a.v).slice(0, 3).map((x) => x.i);
    const hourlyTotals: number[] = Array.from({ length: 24 }, (_, h) => grid.reduce((s, r) => s + r[h], 0));
    const topHours = topNIndexes(hourlyTotals, 3).filter((i) => hourlyTotals[i] > 0).sort((a, b) => a - b);
    return { grid, totalScraps, avgPerDay, criticalDayIndexes, topHours };
}

/**
 * Top důvody zmetků s podílem a delta proti předchozímu období.
 */
export async function getTopScrapReasons(fromIso: string, toIso: string, take = 10, filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }): Promise<TopScrapReasonItem[]> {
    const from = moment(fromIso);
    const to = moment(toIso);
    const span = Math.max(1, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-span, 'minutes');
    const [cur, prev] = await Promise.all([
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, filters),
        ScrapRecordsRepository.fetchShiftScrapRecords(prevFrom.toISOString(), prevTo.toISOString(), filters),
    ]);
    const agg = (list: any[]) => {
        const map = new Map<string | null, { name: string; count: number }>();
        for (const r of list) {
            const id: string | null = r?.scrapReason?.id ?? null;
            const name: string = r?.scrapReason?.name ?? '—';
            const cnt = Number(r?.scrapCount ?? 0) || 0;
            const item = map.get(id) || { name, count: 0 };
            item.count += cnt;
            map.set(id, item);
        }
        const total = Array.from(map.values()).reduce((s, x) => s + x.count, 0);
        return { map, total };
    };
    const curAgg = agg(cur);
    const prevAgg = agg(prev);
    const items: TopScrapReasonItem[] = Array.from(curAgg.map.entries()).map(([id, v]) => {
        const share = curAgg.total > 0 ? (v.count / curAgg.total) * 100 : null;
        const prevShare = prevAgg.total > 0 ? (((prevAgg.map.get(id)?.count || 0) / prevAgg.total) * 100) : null;
        const delta = share != null && prevShare != null ? +(share - prevShare).toFixed(1) : null;
        return { reasonId: id, reasonName: v.name, count: v.count, sharePct: share == null ? null : +share.toFixed(1), shareDeltaPct: delta };
    });
    items.sort((a, b) => b.count - a.count);
    return items.slice(0, take);
}

/**
 * Získá agregovaná data zmetkovitosti podle vybraných dimenzí
 */
export async function getAggregatedScrapData(
    fromIso: string,
    toIso: string,
    dimensions: ScrapAggregationDimension[],
    take = 10,
    filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
): Promise<AggregatedScrapItem[]> {
    const from = moment(fromIso);
    const to = moment(toIso);
    const span = Math.max(1, to.diff(from, 'minutes'));
    const prevTo = from.clone();
    const prevFrom = from.clone().add(-span, 'minutes');
    
    const [current, previous] = await Promise.all([
        ScrapRecordsRepository.fetchShiftScrapRecords(fromIso, toIso, filters),
        ScrapRecordsRepository.fetchShiftScrapRecords(prevFrom.toISOString(), prevTo.toISOString(), filters),
    ]);

    // Získat stations z paměti pro přístup k workplace a location
    const stations = stationsStore.useGetter(CrudGetter.Data) as StationModel[];
    const getStation = (stationId: string | null | undefined) => stations.find(s => s.id === stationId);

    // Vytvoří klíč pro agregaci podle vybraných dimenzí
    const createKey = (r: any): string => {
        const parts: string[] = [];
        const station = getStation(r?.stationId);
        for (const dim of dimensions) {
            switch (dim) {
                case 'location':
                    parts.push(station?.workplace?.locationId || '__no_value__');
                    break;
                case 'workplace':
                    parts.push(station?.workplaceId || '__no_value__');
                    break;
                case 'station':
                    parts.push(r?.stationId || '__no_value__');
                    break;
                case 'project':
                    // Projekt je v shift.shiftProductions[0].projectId
                    parts.push(r?.shift?.shiftProductions?.[0]?.projectId || '__no_value__');
                    break;
                case 'shiftTeam':
                    parts.push(r?.shift?.shiftTeamTypeId || '__no_value__');
                    break;
                case 'shiftType':
                    parts.push(r?.shift?.shiftTypeId || '__no_value__');
                    break;
                case 'scrapReason':
                    parts.push(r?.scrapReasonId || '__no_value__');
                    break;
            }
        }
        return parts.join('|');
    };

    // Agregace dat
    const agg = (list: any[]) => {
        const map = new Map<string, AggregatedScrapItem>();
        for (const r of list) {
            const key = createKey(r);

            let item = map.get(key);
            if (!item) {
                item = { totalScraps: 0, eventCount: 0, changePct: null } as AggregatedScrapItem;
                
                const station = getStation(r?.stationId);
                
                // Naplnit data podle vybraných dimenzí
                for (const dim of dimensions) {
                    switch (dim) {
                        case 'location':
                            item.locationId = station?.workplace?.locationId || '__no_value__';
                            item.locationName = station?.workplace?.location?.name || '--';
                            break;
                        case 'workplace':
                            item.workplaceId = station?.workplaceId || '__no_value__';
                            item.workplaceName = station?.workplace?.name || '--';
                            break;
                        case 'station':
                            item.stationId = r?.stationId || '__no_value__';
                            item.stationName = r?.station?.name || '--';
                            break;
                        case 'project':
                            item.projectId = r?.shift?.shiftProductions?.[0]?.projectId || '__no_value__';
                            item.projectName = r?.shift?.shiftProductions?.[0]?.project?.name || '--';
                            break;
                        case 'shiftTeam':
                            item.shiftTeamTypeId = r?.shift?.shiftTeamTypeId || '__no_value__';
                            item.shiftTeamTypeName = r?.shift?.shiftTeamType?.name || '--';
                            break;
                        case 'shiftType':
                            item.shiftTypeId = r?.shift?.shiftTypeId || '__no_value__';
                            item.shiftTypeName = r?.shift?.shiftType?.name || '--';
                            break;
                        case 'scrapReason':
                            item.scrapReasonId = r?.scrapReasonId || '__no_value__';
                            item.scrapReasonName = r?.scrapReason?.name || '--';
                            break;
                    }
                }
                map.set(key, item);
            }
            item.eventCount += 1; // Počet událostí (záznamů)
            item.totalScraps += Number(r?.scrapCount ?? 0) || 0; // Celkový počet zmetků
        }
        return map;
    };

    const cur = agg(current);
    const prev = agg(previous);
    
    const result: AggregatedScrapItem[] = Array.from(cur.entries()).map(([key, v]) => {
        const p = prev.get(key);
        const changePct = p && p.totalScraps > 0 ? ((v.totalScraps - p.totalScraps) / p.totalScraps) * 100 : null;
        return {
            ...v,
            changePct: changePct == null ? null : +changePct.toFixed(1),
            prevTotalScraps: p?.totalScraps ?? (null as any),
            prevEventCount: p?.eventCount ?? (null as any),
        };
    });
    
    // Výchozí řazení podle celkového počtu zmetků
    result.sort((a, b) => b.totalScraps - a.totalScraps);
    return result.slice(0, take);
}


